/**
 * @genesis_domain Auth Engine
 * @genesis_version V9.1
 * @genesis_intent Login page orchestrator — thin hub (< 100 lines) that delegates to extracted domain components and view models
 * @genesis_boundaries No business logic, no state management, no API calls
 * @genesis_ai_trail V9.0: Refactored from 265-line monolith to thin orchestrator. Login form extracted to LoginForm.tsx, OTP flow extracted to RecoveryOtpForm.tsx + useRecoveryViewModel.ts, view model extracted to useLoginViewModel.ts.
 */

import { motion } from 'framer-motion';
import { Home, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext.js';
import { LoginForm } from '@/domains/auth/components/LoginForm';
import { RecoveryOtpForm } from '@/domains/auth/components/RecoveryOtpForm';
import { useLoginViewModel } from '@/domains/auth/hooks/useLoginViewModel';
import { useRecoveryViewModel } from '@/domains/auth/hooks/useRecoveryViewModel';

import AuthHeader from './components/AuthHeader.js';

export function Login() {
  const { currentUser, isInitializing, error } = useAuth();
  const location = useLocation();
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');

  const {
    register,
    errors,
    isSubmitting,
    onSubmit,
    handleForgotPassword,
    handleGoogleLogin,
    handleGithubLogin,
  } = useLoginViewModel();

  const {
    otpCode,
    setOtpCode,
    isVerifyingOtp,
    otpError,
    handleVerifyOtp,
    resetOtp,
  } = useRecoveryViewModel();

  useEffect(() => {
    if (location.state?.error) {
      setCallbackError(location.state.error);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  if (currentUser) {
    return <Navigate to="/" replace />;
  }

  const onForgotPassword = async () => {
    const emailInput = document.querySelector<HTMLInputElement>('input[name="email"]')?.value;
    if (!emailInput) {
      setCallbackError('Please enter your email address first.');
      return;
    }
    setForgotPasswordEmail(emailInput);
    const result = await handleForgotPassword(emailInput);
    if (result.success) {
      setForgotPasswordSent(true);
      setCallbackError(null);
      resetOtp();
    } else {
      setCallbackError(result.error);
    }
  };

  const onVerifyOtp = () => {
    handleVerifyOtp(forgotPasswordEmail);
  };

  const onBackToSignIn = () => {
    setForgotPasswordSent(false);
    setCallbackError(null);
    resetOtp();
  };

  return (
    <div className="h-full w-full flex items-center justify-center bg-transparent p-6 font-sans">
      <div className="w-full max-w-[1100px] grid grid-cols-1 lg:grid-cols-2 theme-card backdrop-blur-3xl border border-[var(--color-glass-border)] rounded-[32px] shadow-2xl overflow-hidden">
        {/* Left Side - Form */}
        <div className="p-8 lg:p-16 flex flex-col justify-center bg-transparent">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <AuthHeader
              icon={Home}
              title="Welcome Home"
              subtitle="Access your workspace's Home Recipe Vault."
              error={callbackError || error}
            />

            {forgotPasswordSent ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <RecoveryOtpForm
                  otpCode={otpCode}
                  onOtpCodeChange={setOtpCode}
                  isVerifyingOtp={isVerifyingOtp}
                  otpError={otpError}
                  onSubmit={onVerifyOtp}
                  onBack={onBackToSignIn}
                />
              </motion.div>
            ) : (
              <LoginForm
                register={register}
                errors={errors}
                isSubmitting={isSubmitting}
                isInitializing={isInitializing}
                onSubmit={onSubmit}
                onForgotPassword={onForgotPassword}
                onGoogleLogin={handleGoogleLogin}
                onGithubLogin={handleGithubLogin}
              />
            )}

            <p className="text-center mt-10 text-sm text-[var(--color-content-secondary)] font-medium">
              New to the vault?{' '}
              <Link to="/signup" className="text-[var(--color-primary-base)] font-bold hover:underline">
                Create account
              </Link>
            </p>
          </motion.div>
        </div>

        {/* Right Side - Visual/Marketing */}
        <div className="hidden lg:flex relative bg-[var(--color-primary-subtle)] backdrop-blur-2xl border-l border-[var(--color-glass-border)] p-16 flex-col justify-between text-[var(--color-content-primary)] overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2)_0%,transparent_50%)]" />
          </div>

          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--color-surface-elevated)] backdrop-blur-md rounded-full text-xs font-bold uppercase tracking-widest mb-8 text-[var(--color-glass-text)]"
            >
              <ShieldCheck size={14} className="text-[var(--color-glass-text)] opacity-60" />
              Trusted by workspaces worldwide
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-5xl font-bold leading-tight mb-6"
            >
              Every recipe <br /> tells a story. <br /> Keep yours alive.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-[var(--color-glass-text)] opacity-80 text-lg font-medium max-w-md italic"
            >
              "The kitchen is the heart of the home, and these recipes are its soul. Preserve your
              workspace's culinary heritage for generations to come."
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="relative z-10 bg-[var(--color-surface-elevated)] backdrop-blur-xl rounded-2xl p-8 border border-[var(--color-glass-border)]"
          >
            <div className="flex items-center gap-6 mb-8">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map(userNum => (
                  <div
                    key={`user-avatar-${userNum}`}
                    className="w-10 h-10 rounded-full border-2 border-[var(--color-primary-base)]/30 bg-[var(--color-surface-elevated)] overflow-hidden"
                  >
                    <img
                      src={`https://picsum.photos/seed/user${userNum}/100/100`}
                      alt="User"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ))}
              </div>
              <div>
                <p className="font-bold">Preserving Workspace Legacies</p>
                <p className="text-xs text-[var(--color-glass-text)] opacity-60">9,240 Memories Shared</p>
              </div>
            </div>
            <div className="h-2 w-full bg-[var(--color-surface-elevated)] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '75%' }}
                transition={{ delay: 1, duration: 1.5 }}
                className="h-full bg-[var(--color-primary-base)] rounded-full"
              />
            </div>
          </motion.div>
          <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[var(--color-surface-elevated)] rounded-full blur-[100px] opacity-50" />
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-[var(--color-surface-elevated)] rounded-full blur-[80px] opacity-30" />
        </div>
      </div>
    </div>
  );
}
